import { useState, useEffect, useCallback } from 'react';
import { type AppPlan } from '../lib/planLimits';

/** All available chart widget IDs. */
export type WidgetId =
  | 'avgHeartRate'
  | 'avgRespRate'
  | 'weight'
  | 'heartRate'
  | 'respRate'
  | 'mucousMembrane'
  | 'temp'
  | 'fluid'
  | 'foodIntake'
  | 'avgWeight'
  | 'avgMucousMembrane'
  | 'avgWaterIntake'
  | 'avgTemperature'
  | 'avgFoodIntake';

export interface WidgetConfig {
  id: WidgetId;
  visible: boolean;
}

/** Widgets available on the Vital plan (fixed order, not user-customisable). */
const VITAL_WIDGETS: WidgetConfig[] = [
  { id: 'avgHeartRate', visible: true },
  { id: 'avgRespRate', visible: true },
  { id: 'heartRate', visible: true },
  { id: 'respRate', visible: true },
  { id: 'weight', visible: true },
  { id: 'mucousMembrane', visible: true },
  { id: 'temp', visible: true },
  { id: 'fluid', visible: true },
];

/** Full default widget set for Lifetime (user can reorder / toggle). */
const LIFETIME_DEFAULTS: WidgetConfig[] = [
  { id: 'avgHeartRate', visible: true },
  { id: 'avgRespRate', visible: true },
  { id: 'heartRate', visible: true },
  { id: 'respRate', visible: true },
  { id: 'weight', visible: true },
  { id: 'mucousMembrane', visible: true },
  { id: 'temp', visible: true },
  { id: 'fluid', visible: true },
  { id: 'foodIntake', visible: false },
  { id: 'avgWeight', visible: false },
  { id: 'avgMucousMembrane', visible: false },
  { id: 'avgTemperature', visible: false },
  { id: 'avgWaterIntake', visible: false },
  { id: 'avgFoodIntake', visible: false },
];

const VALID_WIDGET_IDS = new Set<WidgetId>([
  'avgHeartRate',
  'avgRespRate',
  'weight',
  'heartRate',
  'respRate',
  'mucousMembrane',
  'temp',
  'fluid',
  'foodIntake',
  'avgWeight',
  'avgMucousMembrane',
  'avgWaterIntake',
  'avgTemperature',
  'avgFoodIntake',
]);

const STORAGE_KEY = 'koda_trend_widgets';

function loadFromStorage(): WidgetConfig[] | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = (JSON.parse(raw) as WidgetConfig[]).filter(w => VALID_WIDGET_IDS.has(w.id));
    // Validate basic shape
    if (!Array.isArray(parsed) || !parsed.every(w => w.id && typeof w.visible === 'boolean')) return null;
    const hasLegacyOrder = !parsed.some(w => w.id === 'avgHeartRate') || !parsed.some(w => w.id === 'avgRespRate');
    if (hasLegacyOrder) {
      const visibleById = new Map(parsed.map(w => [w.id, w.visible] as const));
      return LIFETIME_DEFAULTS.map(w => ({
        id: w.id,
        visible: visibleById.get(w.id) ?? w.visible,
      }));
    }
    const oldDefaultOrder: WidgetId[][] = [
      // v1 default (weight before heartRate)
      ['avgHeartRate','avgRespRate','weight','heartRate','respRate','mucousMembrane','temp','fluid','avgWeight','avgMucousMembrane','avgWaterIntake','avgTemperature'],
      // v2 default (heartRate before weight, fluid hidden, no food)
      ['avgHeartRate','avgRespRate','heartRate','respRate','mucousMembrane','weight','temp','fluid','avgWeight','avgMucousMembrane','avgWaterIntake','avgTemperature'],
    ];
    const isOldDefaultOrder = oldDefaultOrder.some(order => order.every((id, idx) => parsed[idx]?.id === id));
    if (isOldDefaultOrder) {
      const visibleById = new Map(parsed.map(w => [w.id, w.visible] as const));
      return LIFETIME_DEFAULTS.map(w => ({
        id: w.id,
        visible: visibleById.get(w.id) ?? w.visible,
      }));
    }
    // Ensure any new default widgets that aren't in stored config get appended
    const storedIds = new Set(parsed.map(w => w.id));
    const missing = LIFETIME_DEFAULTS.filter(w => !storedIds.has(w.id));
    return [...parsed, ...missing];
  } catch {
    return null;
  }
}

function saveToStorage(widgets: WidgetConfig[]) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(widgets));
  } catch {
    // Storage unavailable — ignore
  }
}

/**
 * Manages trend widget visibility and order.
 *
 * - For Vital plan: returns a fixed, non-customisable set (3 charts).
 * - For Lifetime plan: returns user-configured set (stored in localStorage).
 *
 * Only `isLifetime` users can call `setWidgets` / `resetToDefaults`.
 */
export function useTrendConfig(plan: AppPlan) {
  const isLifetime = plan === 'lifetime';

  const [widgets, setWidgetsState] = useState<WidgetConfig[]>(() => {
    if (!isLifetime) return VITAL_WIDGETS;
    return loadFromStorage() ?? LIFETIME_DEFAULTS;
  });

  // Re-sync when plan changes (e.g. after subscription loads)
  useEffect(() => {
    if (!isLifetime) {
      setWidgetsState(VITAL_WIDGETS);
    } else {
      setWidgetsState(loadFromStorage() ?? LIFETIME_DEFAULTS);
    }
  }, [isLifetime]);

  const setWidgets = useCallback(
    (next: WidgetConfig[] | ((prev: WidgetConfig[]) => WidgetConfig[])) => {
      if (!isLifetime) return; // guard — Vital plan cannot customise
      setWidgetsState(prev => {
        const updated = typeof next === 'function' ? next(prev) : next;
        saveToStorage(updated);
        return updated;
      });
    },
    [isLifetime],
  );

  const resetToDefaults = useCallback(() => {
    if (!isLifetime) return;
    saveToStorage(LIFETIME_DEFAULTS);
    setWidgetsState(LIFETIME_DEFAULTS);
  }, [isLifetime]);

  /** Move a widget up or down in the list. */
  const moveWidget = useCallback(
    (id: WidgetId, direction: 'up' | 'down') => {
      setWidgets(prev => {
        const idx = prev.findIndex(w => w.id === id);
        if (idx === -1) return prev;
        const next = [...prev];
        const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
        if (swapIdx < 0 || swapIdx >= next.length) return prev;
        [next[idx], next[swapIdx]] = [next[swapIdx], next[idx]];
        return next;
      });
    },
    [setWidgets],
  );

  /** Toggle a single widget's visibility. */
  const toggleWidget = useCallback(
    (id: WidgetId) => {
      setWidgets(prev =>
        prev.map(w => (w.id === id ? { ...w, visible: !w.visible } : w)),
      );
    },
    [setWidgets],
  );

  return {
    widgets,
    setWidgets,
    resetToDefaults,
    moveWidget,
    toggleWidget,
    isCustomisable: isLifetime,
  };
}
